import axios from '../../axios'
import i18n from '../../i18n'

const state = {
    settings: {},
    loading: false,
    error: null
}

const getters = {
    allSettings: state => state.settings,
    groupedSettings: state => {
        const grouped = {}
        Object.values(state.settings).forEach(setting => {
            if (!grouped[setting.group]) {
                grouped[setting.group] = []
            }
            grouped[setting.group].push(setting)
        })
        return grouped
    },
    getSetting: state => id => state.settings[id],
    darkMode: state => {
        const mode = state.settings['mode']
        return mode ? mode.value === 'Dark' : false
    },
    isLoading: state => state.loading,
    error: state => state.error
}

const actions = {
    async fetchSettings({ commit }) {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        try {
            const response = await axios.get('/api/settings')
            // Response is grouped by default from API, but we want a flat map for easier updates
            // However, the API returns { "Group 1": [ ... ], "Group 2": [ ... ] }
            // We should probably normalize it or just store it as is and provide getters.
            // Wait, the API returns grouped settings. Let's adjust the state to match or flatten it.
            // Flattening is better for "getSetting('mode')".

            const flatSettings = {}
            Object.entries(response.data).forEach(([group, items]) => {
                items.forEach(item => {
                    flatSettings[item.id] = { ...item, group }
                })
            })

            commit('SET_SETTINGS', flatSettings)

            // Apply theme immediately after fetching
            const modeSetting = flatSettings['mode']
            if (modeSetting) {
                commit('APPLY_THEME', modeSetting.value)
            }
            // Apply language
            const langSetting = flatSettings['language']
            if (langSetting && langSetting.value) {
                commit('APPLY_LANGUAGE', langSetting.value)
            }
        } catch (error) {
            console.error('Error fetching settings:', error)
            commit('SET_ERROR', 'Failed to load settings')
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async updateSetting({ commit, dispatch }, { id, value }) {
        // Optimistic update
        commit('UPDATE_SETTING_VALUE', { id, value })

        // Apply theme immediately if it's the mode setting
        if (id === 'mode') {
            commit('APPLY_THEME', value)
        }
        if (id === 'language') {
            commit('APPLY_LANGUAGE', value)
        }

        try {
            return await axios.put(`/api/settings/${id}`, { value })
        } catch (error) {
            console.error(`Error updating setting ${id}:`, error)
            throw error // Re-throw so components can handle it
        }
    }
}

const mutations = {
    SET_SETTINGS(state, settings) {
        state.settings = settings
    },
    SET_LOADING(state, loading) {
        state.loading = loading
    },
    SET_ERROR(state, error) {
        state.error = error
    },
    UPDATE_SETTING_VALUE(state, { id, value }) {
        if (state.settings[id]) {
            state.settings[id].value = value
        }
    },
    APPLY_THEME(state, mode) {
        if (mode === 'Dark') {
            document.documentElement.setAttribute('data-bs-theme', 'dark')
        } else {
            document.documentElement.setAttribute('data-bs-theme', 'light')
        }
    },
    APPLY_LANGUAGE(state, lang) {
        // Handle vue-i18n composition api (ref) or legacy
        if (i18n.global.locale.value !== undefined) {
            i18n.global.locale.value = lang
        } else {
            i18n.global.locale = lang
        }
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
